import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

const DialogLoadingContext = React.createContext(false);

// Track how many dialogs are open — used to dim sheets behind dialogs
let _dialogOpenCount = 0;
function _incDialog() {
  _dialogOpenCount++;
  document.body.classList.add('dialog-is-open');
}
function _decDialog() {
  _dialogOpenCount = Math.max(0, _dialogOpenCount - 1);
  if (_dialogOpenCount === 0) document.body.classList.remove('dialog-is-open');
}

function Dialog({
  loading = false,
  open,
  onOpenChange,
  ...props
}: DialogPrimitive.Root.Props & { loading?: boolean }) {
  const handleOpenChange: DialogPrimitive.Root.Props['onOpenChange'] = (
    next,
    eventDetails,
  ) => {
    if (loading && !next) return;
    if (next) _incDialog();
    else _decDialog();
    onOpenChange?.(next, eventDetails);
  };

  // Sync with controlled `open` prop
  React.useEffect(() => {
    if (open) {
      _incDialog();
      return () => {
        _decDialog();
      };
    }
  }, [open]);

  return (
    <DialogLoadingContext.Provider value={loading}>
      <DialogPrimitive.Root
        data-slot='dialog'
        open={open}
        disablePointerDismissal={loading}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogLoadingContext.Provider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot='dialog-overlay'
      className={cn(
        'fixed inset-0 z-200 bg-black/80 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  );
}

type DialogSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<DialogSize, string> = {
  sm: 'sm:max-w-md sm:min-h-[400px] max-h-[70vh]',
  md: 'sm:max-w-[712px] sm:min-h-[480px] max-h-[70vh]',
  lg: 'sm:max-w-5xl h-[780px] max-h-[70vh]',
};

// Used when no `size` is passed — preserves the historical chunky default
// (~1024px wide, fixed 780px tall) so existing dialogs that rely on it don't
// break. New dialogs should always pass an explicit `size`.
const LEGACY_DEFAULT = 'sm:max-w-5xl h-[780px] max-h-[70vh]';

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  size?: DialogSize;
}) {
  const loading = React.useContext(DialogLoadingContext);
  const renderClose = showCloseButton && !loading;
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot='dialog-content'
        {...(size ? { 'data-size': size } : {})}
        className={cn(
          '@container/dialog fixed top-1/2 left-1/2 z-200 w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-popover text-xs/relaxed text-popover-foreground shadow-modal ring-1 ring-foreground/10 duration-100 outline-none flex flex-col gap-0 p-0 overflow-hidden data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          size ? SIZE_CLASSES[size] : LEGACY_DEFAULT,
          className,
        )}
        {...props}
      >
        {children}
        {renderClose && (
          <DialogPrimitive.Close
            data-slot='dialog-close'
            render={
              <Button
                variant='ghost'
                className='absolute top-2 right-2'
                size='icon-sm'
              />
            }
          >
            <FontAwesomeIcon icon={faXmark} />
            <span className='sr-only'>Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-header'
      className={cn(
        'flex flex-col gap-1 px-6 py-4 border-b border-border/60 shrink-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='dialog-body'
      className={cn('flex-1 min-h-0 overflow-y-auto px-6 py-5', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end px-6 py-4 border-t border-border/60 shrink-0',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant='outline' />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot='dialog-title'
      className={cn('font-heading text-base font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot='dialog-description'
      className={cn(
        'text-xs/relaxed text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
